import { useMemo, useRef, useState } from "react";
import {
  Box,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { addYears } from "date-fns";
import CalenderComponent from "../../calender/Calender";
import { formatDateFn } from "../../../helper/common/formatDateForAPI";
import { getDefaultCalendarDate } from "../../../constant/calendarDefaultConfigurations";

const parseStoredDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const FilterDateInput = ({
  value = [],
  onChange,
  placeholder = "Select date",
  error = false,
}) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [localCalendarDate, setLocalCalendarDate] = useState(
    getDefaultCalendarDate(false)
  );
  const [localDateDifference, setLocalDateDifference] = useState("Custom");
  const [localOldCalendarData, setLocalOldCalendarData] = useState(
    getDefaultCalendarDate(false)
  );
  const [localOldDateDifference, setLocalOldDateDifference] = useState("Custom");
  const [localApplyCalendar, setLocalApplyCalendar] = useState(1);

  const localCalendarState = useMemo(
    () => ({
      calendarDate: localCalendarDate,
      setCalendarDate: setLocalCalendarDate,
      dateDifference: localDateDifference,
      setDateDifference: setLocalDateDifference,
      oldCalendarData: localOldCalendarData,
      setOldCalendarData: setLocalOldCalendarData,
      oldDateDifference: localOldDateDifference,
      setOldDateDifference: setLocalOldDateDifference,
      applyCalendar: localApplyCalendar,
      setApplyCalendar: setLocalApplyCalendar,
    }),
    [
      localCalendarDate,
      localDateDifference,
      localOldCalendarData,
      localOldDateDifference,
      localApplyCalendar,
    ]
  );

  const storedValue = Array.isArray(value) ? value[0] : value;
  const selectedDate = parseStoredDate(storedValue);
  const displayLabel = selectedDate
    ? formatDateFn(selectedDate, true)
    : placeholder;

  const handleDateChange = (date) => {
    if (!date) return;
    onChange?.([formatDateFn(date, true)]);
    setOpen(false);
  };

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={() => setOpen(true)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1,
          py: 0.5,
          minHeight: 32,
          cursor: "pointer",
          bgcolor: "#fff",
          borderTopRightRadius: "8px",
          borderBottomRightRadius: "8px",
          border: error ? "1px solid #FF4D4F" : "none",
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            color: selectedDate ? "text.primary" : "text.secondary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {displayLabel}
        </Typography>
        <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: "#919191" }} />
      </Box>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: (theme) => (theme?.zIndex?.modal ?? 1300) + 2 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            elevation={6}
            sx={{
              mt: 0.5,
              overflow: "hidden",
              "& .calender-class": { position: "static" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                px: 0.5,
                pt: 0.5,
              }}
            >
              <IconButton size="small" onClick={() => setOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <CalenderComponent
              singleDatePicker
              localCalendar
              localCalendarState={localCalendarState}
              date={selectedDate || new Date()}
              onSingleDateChange={handleDateChange}
              singlePickerMinDate={addYears(new Date(), -10)}
              singlePickerMaxDate={new Date()}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default FilterDateInput;
